import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Redirect, Route, RouteComponentProps, Switch} from 'react-router-dom'

import {Navigator} from '@progyny/components/Navigator'
import {
  SetupProfileMutationVariables,
  SetupMenopauseProfileMutationVariables,
  SetupPregnancyPostpartumProfileMutationVariables,
} from '@progyny/generated/graphql'
import useOnboardingMutations from '@progyny/hooks/useOnboardingMutations'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useProgressColor} from '@progyny/hooks/useProgressColor'
import CommonContainer from '@progyny/pages/CreateAccount/CommonContainer'
import useSetupProfileRoutes from '@progyny/pages/SetUpProfile/hooks/useSetupProfileRoutes'
import {SetupProfileRoutes} from '@progyny/pages/SetUpProfile/types'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

import {UNSET} from '../../const'

const SetUpProfile: React.FC<RouteComponentProps> = (props) => {
  // Provide default value for create account procedure
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const onboardingData = useMeStore((s) => s.onboardingData)
  const {history, match} = props
  const url = match.url
  const {t} = useTranslation()
  const {routes: subRoutes, navigateTo} = useSetupProfileRoutes()
  const {productLine} = useProductLine()
  const progressColor = useProgressColor()
  let subRouteIndex = SetupProfileRoutes.COMPLETE

  // Determine which step to show based on product line
  if (productLine === 'menopause') {
    if (onboardingData.menstrualPeriodStatus === UNSET) {
      subRouteIndex = SetupProfileRoutes.BIRTH_ASSIGNED_SEX // This will be menstrual period
    } else if (onboardingData.lastMenstrualPeriod === UNSET) {
      subRouteIndex = SetupProfileRoutes.GENDER_IDENTITY // This will be last menstrual
    } else if (onboardingData.menopauseSymptoms === UNSET) {
      subRouteIndex = SetupProfileRoutes.RELATIONSHIP_DYNAMIC // This will be menopause symptoms
    }
  } else {
    // Fertility journey logic
    if (onboardingData.birthAssignedSex === UNSET) {
      subRouteIndex = SetupProfileRoutes.BIRTH_ASSIGNED_SEX
    } else if (onboardingData.genderIdentity === UNSET) {
      subRouteIndex = SetupProfileRoutes.GENDER_IDENTITY
    } else if (onboardingData.relationshipDynamic === UNSET) {
      subRouteIndex = SetupProfileRoutes.RELATIONSHIP_DYNAMIC
    } else if (onboardingData.familyFormingJourney === UNSET) {
      subRouteIndex = SetupProfileRoutes.FAMILY_FORMING_JOURNEY
    }
  }

  const {
    updateLoading,
    persistSetupInformation,
    persistMenopauseProfileInformation,
    persistPregnancyPostpartumProfileInformation,
  } = useOnboardingMutations()

  // Create a wrapper function that can handle all mutation types
  const handlePersistMemberData = (
    variables:
      | SetupProfileMutationVariables
      | SetupMenopauseProfileMutationVariables
      | SetupPregnancyPostpartumProfileMutationVariables,
    onSuccess: React.Dispatch<void>
  ) => {
    if (productLine === 'menopause') {
      persistMenopauseProfileInformation(
        variables as SetupMenopauseProfileMutationVariables,
        onSuccess
      )
    } else if (productLine === 'pregnancy_and_postpartum') {
      persistPregnancyPostpartumProfileInformation(
        variables as SetupPregnancyPostpartumProfileMutationVariables,
        onSuccess
      )
    } else {
      persistSetupInformation(
        variables as SetupProfileMutationVariables,
        onSuccess
      )
    }
  }

  const {getPath} = useRoutes()
  const evaluateProfileForProductLine = useMeStore(
    (s) => s.evaluateProfileForProductLine
  )
  const isAccountCreated = useMeStore((s) => s.isAccountCreated)

  // Check if onboarding is complete for the CURRENT product line
  // This uses the centralized evaluateProfileForProductLine function
  // Also need to check isAccountCreated (same as isAuthorized = isAccountCreated && isProfileSetup)
  const isCurrentProductLineOnboardingComplete = useMemo(() => {
    return isAccountCreated && evaluateProfileForProductLine(productLine)
  }, [productLine, evaluateProfileForProductLine, isAccountCreated])

  return (
    <Switch>
      {subRoutes.map((route, idx) => {
        const RouteComponent = route.component

        return (
          <Route
            key={idx}
            path={`${url}${route.path}`}
            render={() => (
              <CommonContainer
                progressBarTitle={
                  route.showProgressBar === false
                    ? undefined
                    : productLine === 'menopause'
                      ? t('setup_profile_your_health_profile')
                      : t('setup_profile_your_family_plan')
                }
                headerTitle={route.headerTitle && t(route.headerTitle)}
                description={route.description && t(route.description)}
                progressValue={((idx + 1) / (subRoutes.length + 1)) * 100}
                progressColor={progressColor}
              >
                <RouteComponent
                  navigator={Navigator}
                  onboardingData={onboardingData}
                  callback={() => {
                    // Check if next route exists before navigating
                    if (subRoutes[idx + 1]) {
                      history.push(`${url}${subRoutes[idx + 1].path}`)
                    }
                  }}
                  updateLoading={updateLoading}
                  persistMemberData={handlePersistMemberData}
                  goBack={() => {
                    if (idx - 1 < SetupProfileRoutes.BIRTH_ASSIGNED_SEX) {
                      return
                    } else if (subRoutes[idx - 1]) {
                      history.push(`${url}${subRoutes[idx - 1].path}`)
                    }
                  }}
                  match={match}
                  history={history}
                  navigateTo={navigateTo}
                />
              </CommonContainer>
            )}
          />
        )
      })}
      <Route exact path={match.url}>
        {isCurrentProductLineOnboardingComplete ? (
          <Redirect to={getPath(RouteEnum.HOME)} />
        ) : isLoggedIn && subRoutes[subRouteIndex] ? (
          <Redirect to={`${url}${subRoutes[subRouteIndex].path}`} />
        ) : isLoggedIn ? (
          // Fallback: redirect to first route if subRouteIndex is out of bounds
          subRoutes.length > 0 ? (
            <Redirect to={`${url}${subRoutes[0].path}`} />
          ) : (
            <Redirect to={getPath(RouteEnum.HOME)} />
          )
        ) : (
          <Redirect to={getPath(RouteEnum.LOGIN)} />
        )}
      </Route>
    </Switch>
  )
}
export default SetUpProfile
